import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import Http from "../../Http";
import { Loan_API } from "../../Helper";
// Import Icon from lucide-react
import { ChevronDown } from "lucide-react";
import UniversalLoanLoader from "../Loading/UniversalLoanLoader";

function Icon({ id, open }) {
  return (
    <ChevronDown
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    />
  );
}

const EducationLoanDetail = () => {
  const { pageUrl } = useParams();
  const [loanDetail, setLoanDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [activeIndex, setActiveIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(
    loanDetail?.loan_faq?.length - 1 || 0
  );
  useEffect(() => {
    fetchLoanDetail();
  }, [pageUrl]);

  const fetchLoanDetail = async () => {
    try {
      const response = await Http.get(Loan_API);

      // Check if the API returns data in the expected format
      const loanArray = response.loan || response;

      if (!Array.isArray(loanArray)) {
        throw new Error("Invalid data format received from API");
      }

      // const detail = loanArray.find(item => item.page_url === pageUrl);
      const detail = loanArray.find(
        (item) =>
          item.page_url === pageUrl && item.category_name === "Education Loan" // This ensures we only get Car insurance items
      );
      if (detail) {
        setLoanDetail(detail);
      } else {
        setError("Loan detail not found");
      }
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch loan detail: " + error.message);
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      setActiveIndex(null);
    } else {
      setActiveIndex(index);
    }
  };

  if (loading) {
    return (
      <UniversalLoanLoader
        type="education"
        title="Processing Education Loan Application"
        subtitle="Investing in your bright future..."
        theme="blue"
      />
    );
  }

  if (loading)
    return (
      <div className="text-center py-10">Loading insurance details...</div>
    );
  if (error)
    return <div className="text-center py-10 text-red-600">{error}</div>;
  if (!loanDetail)
    return <div className="text-center py-10">Insurance details not found</div>;

  return (
    <>
      <Helmet>
        <title>{loanDetail.metatitle || loanDetail.page_title}</title>
        <meta
          name="description"
          content={loanDetail.metadesc}
        />
        <meta name="keywords" content={loanDetail.metakeyword} />
        <link
          rel="canonical"
          href={`https://thefinmaster.com/education-loan/${pageUrl}`}
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>

      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="education_insurance"
              className="text-blue-600 hover:text-blue-800 "
            >
              Car Insurance
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">{loanDetail.page_title}</span>
          </li>
        </ol>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          <div className="md:w-1/3">
            <img
              src={loanDetail.image}
              alt={loanDetail.page_title}
              className="w-full rounded-lg shadow-lg"
            />
          </div>
          <div className="md:w-2/3">
            <h1 className="text-3xl font-bold mb-4">{loanDetail.page_title}</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-1">Interest Rate</p>
                <p className="text-2xl font-bold">
                  {loanDetail.interest_rate}%
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-1">Loan Amount </p>
                <p className="text-2xl font-bold">₹{loanDetail.loan_amount}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-1">Processing Fee</p>
                <p className="text-2xl font-bold">
                  ₹{loanDetail.processing_fee}
                </p>
              </div>
            </div>

            <a
              href={loanDetail.bank_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <button className="bg-green-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300 mr-4">
                Start Application
              </button>
            </a>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6">
            About {loanDetail.page_title}
          </h2>
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: loanDetail.page_details }}
          />
        </div>

        {loanDetail.loan_faq && loanDetail.loan_faq.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {loanDetail.loan_faq.map((faq, index) => (
                <div
                  key={index}
                  className="border rounded-lg overflow-hidden shadow-md"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                  >
                    {faq.question}
                    <Icon id={index} open={activeIndex} />
                  </button>
                  {activeIndex === index && (
                    <div className="p-4 bg-gray-100">
                      <p className="text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EducationLoanDetail;
